import { OFFER_TYPE_HOTEL, OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2, OFFER_TYPE_HOTEL_SLUG, OFFER_TYPE_TOUR_SLUG, OFFER_TYPE_VILLA, OFFER_TYPE_BED_BANK } from 'constants/offer'
import { urlTransform, humanTransform } from 'lib/string/removeSpaceUtils'
import config from 'constants/config'
import { resolveLocalizedConfigValue } from 'constants/config/resolveLocalizedConfigValue'
import { nonNullable } from 'lib/array/arrayUtils'
import productLabels from 'i18n/productLabels'
import { defineMessages } from 'react-intl'
import type { IntlShape } from 'react-intl'

const placesShortlist = [
  'agra',
  'uae',
  'abu dhabi',
  'spain',
  'south africa',
  'south america',
  'sri lanka',
  'indonesia',
  'switzerland',
  'california',
  'hua hin',
  'bhutan',
  'tunisia',
  'northern territory',
  'turkey',
  'slovenia',
  'ethiopia',
  'tahiti',
  'new caledonia',
  'nepal',
  'russia',
  'gold coast',
  'delhi',
  'scandinavia',
  'myanmar',
  'queensland',
  'scotland',
  'usa',
  'canada',
  'new york',
  'caribbean',
  'mauritius',
  'new south wales',
  'montenegro',
  'khao lak',
  'phillipines',
  'central america',
  'egypt',
  'koh samui',
  'oman',
  'asia',
  'croatia',
  'greece',
  'netherlands',
  'middle east',
  'korea',
  'victoria',
  'czech republic',
  'western australia',
  'cambodia',
  'malta',
  'japan',
  'morocco',
  'china',
  'mexico',
  'act',
  'united kingdom',
  'ireland',
  'singapore',
  'vietnam',
  'seychelles',
  'new zealand',
  'dubai',
  'alaska',
  'poland',
  'fiji',
  'krabi',
  'iceland',
  'portugal',
  'vanuatu',
  'maldives',
  'france',
  'romania',
  'malaysia',
  'south australia',
  'qatar',
  'cook islands',
  'hungary',
  'austria',
  'germany',
  'belgium',
  'south pacific',
  'lombok',
  'india',
  'laos',
  'slovakia',
  'europe',
  'cyprus',
  'bangkok',
  'italy',
  'tasmania',
  'phuket',
  'samoa',
  'hong kong',
  'bali',
  'north america',
  'thailand',
  'hawaii',
  'africa',
  'australia',
  'brazil',
  'goa',
  'americas',
  'philippines',
  'rajasthan',
  'georgia',
  'taiwan',
  'tropical north queensland',
  'armenia',
]

export const landingPageProductNames = [
  'Holiday Packages',
  'Hotels',
  'Tours',
  'Holiday Homes',
]

export const PRODUCT_TYPE_HOTEL = 'Hotels'
export const PRODUCT_TYPE_TOUR = 'Tours'

const destinationProductPageMessages = defineMessages({
  holidayPackages: {
    id: 'hgCkhW7b8ReQszqfVQWy',
    defaultMessage: 'Holiday Packages',
    description: 'Product label for holiday packages shown on destination product pages and related travel links',
  },
  holidayHomes: {
    id: 'pGboMpShQm-2lNWez1dR',
    defaultMessage: 'Holiday Homes',
    description: 'Product label for holiday homes shown on destination product pages and related travel links',
  },
})

function formatLandingPageProductName(intl: IntlShape, productName: string): string {
  switch (productName) {
    case 'Holiday Packages':
      return intl.formatMessage(destinationProductPageMessages.holidayPackages)
    case 'Hotels':
      return intl.formatMessage(productLabels.hotels)
    case 'Tours':
      return intl.formatMessage(productLabels.tours)
    case 'Holiday Homes':
      return intl.formatMessage(destinationProductPageMessages.holidayHomes)
    default:
      return productName
  }
}

export const escapeTypes = [
  'Adults-only',
  'All-inclusive',
  'Boutique',
  'City break',
  'Cruises',
  'Family Friendly',
  'Group',
  'Homes & Villas',
  'Honeymoon',
  'Party vibe',
  'Relaxed',
  'Romantic',
  'School holiday',
  'Short Stay',
  'Ski',
  'Spa break',
  'Sun & Beach',
  'Tours',
  'Tours and cruises',
  'Tours by Luxury Escapes',
  'Villa',
]

const escapeTypesWithoutSpace = escapeTypes.map<string>(urlTransform)
const landingPageProductNamesWithoutSpace = landingPageProductNames.map<string>(urlTransform)

function getPageTypeSlug(productName: string) {
  let pageType = OFFER_TYPE_HOTEL_SLUG
  if (productName === 'Tours') {
    pageType = OFFER_TYPE_TOUR_SLUG
  }
  return pageType
}

function calculatePageTypes(productType: string) {
  let pageTypes: Array<App.OfferListOfferType> = []
  if (productType === 'Holiday Homes') {
    pageTypes = [OFFER_TYPE_HOTEL, OFFER_TYPE_VILLA, OFFER_TYPE_BED_BANK]
  } else if (productType === 'Tours') {
    pageTypes = [OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2]
  } else if (productType === 'Holiday Packages') {
    pageTypes = [OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_VILLA, OFFER_TYPE_HOTEL, OFFER_TYPE_BED_BANK]
  } else {
    pageTypes = [OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_VILLA, OFFER_TYPE_HOTEL, OFFER_TYPE_BED_BANK]
  }
  return pageTypes
}

function calculateFilterTypes(productType: string) {
  let filterTypes: Array<string> = []
  if (productType === 'Holiday Homes') {
    filterTypes = ['Homes & Villas']
  }
  return filterTypes
}

function restoreCapitalisation(location: string | undefined, canonicalName?: string | undefined): string | undefined {
  if (canonicalName) {
    return canonicalName
  }
  if (location === undefined) {
    return undefined
  }
  if (location === 'usa') {
    return 'USA'
  }
  if (location == 'act') {
    return 'ACT'
  }
  return location.replace(/\b[a-z]/g, x => x.toUpperCase())
}

function getDestinationProductPage(
  location: string | undefined,
  holidayType: string | undefined,
  productType: string,
  canonicalName: string | undefined,
  intl: IntlShape,
) {
  const pageTypes = calculatePageTypes(productType)
  const filterTypes = calculateFilterTypes(productType)
  const loc = restoreCapitalisation(location, canonicalName)
  const productTypeLabel = formatLandingPageProductName(intl, productType)
  let title: string
  if (loc && holidayType) {
    title = intl.formatMessage(
      {
        id: 'XdAnNJ67OS9Plph17KEA',
        defaultMessage: '{location} {holidayType} {productType}',
        description: 'Destination product page title with location and holiday type',
      },
      { location: loc, holidayType, productType: productTypeLabel },
    )
  } else if (loc) {
    title = intl.formatMessage(
      {
        id: 'd7FWn3Gc2pwYFI5JrAj4',
        defaultMessage: '{location} {productType}',
        description: 'Destination product page title with location only in the offer section',
      },
      { location: loc, productType: productTypeLabel },
    )
  } else if (holidayType) {
    title = intl.formatMessage(
      {
        id: 'at7S6JITgS71gQ2i3LZP',
        defaultMessage: '{holidayType} {productType}',
        description: 'Destination product page title with holiday type only',
      },
      { holidayType, productType: productTypeLabel },
    )
  } else {
    title = productTypeLabel
  }
  if (productType === 'Hotels' && config.BRAND === 'luxuryescapes') {
    if (!loc) {
      title = intl.formatMessage({
        id: '00Yf6ukeAKXpDovioVPF',
        defaultMessage: 'Luxury Hotels',
        description: 'Destination product page title for luxury hotels without location',
      })
    }
    else {
      title = intl.formatMessage(
        {
          id: 'MKTcxxWREjy2RCw_0E9M',
          defaultMessage: 'Find luxury hotels in {location}',
          description: 'Destination product page title for luxury hotels with a specific location',
        },
        { location: loc.split(',')[0] },
      )
    }
  }

  return {
    title,
    places: loc ? [loc] : [],
    holidayTypes: filterTypes,
    pageTypes,
  }
}

function getBaseSubheading(productType: string, intl: IntlShape): string {
  if (config.BRAND === 'luxuryescapes' && productType === 'Hotels') {
    return intl.formatMessage({
      id: 'HANqwHzSb6yEM-1O49nf',
      defaultMessage: 'Escape with our luxury hotel deals',
      description: 'Subheading for luxury hotel destination product page',
    })
  }
  switch (productType) {
    case 'Holiday Packages':
      return intl.formatMessage({
        id: '6Y-WCfZf7z4BOf9g7-rP',
        defaultMessage: 'Explore our Holiday Package deals',
        description: 'Subheading for holiday package destination product page',
      })
    case 'Hotels':
      return intl.formatMessage({
        id: 'Ec_KnGfjdp5GQbwRJbz0',
        defaultMessage: 'Explore our Hotel deals',
        description: 'Subheading for hotel destination product page in the offer section',
      })
    case 'Tours':
      return intl.formatMessage({
        id: 'NBSWeoFyMRnYORZUrMwL',
        defaultMessage: 'Explore our Tour deals',
        description: 'Subheading for tour destination product page on the tour page',
      })
    case 'Holiday Homes':
      return intl.formatMessage({
        id: '_eWbcE_8uaONSi8saurR',
        defaultMessage: 'Explore our Holiday Home deals',
        description: 'Subheading for holiday home destination product page',
      })
    default:
      return intl.formatMessage(
        {
          id: '5qqyDAWjsb8i6t-wiV_t',
          defaultMessage: 'Explore our {productType} deals',
          description: 'Fallback subheading for destination product page with unknown product type',
        },
        { productType },
      )
  }
}

function getSubheadingWithLocation(baseSubheading: string, productType: string, loc: string, intl: IntlShape): string {
  if (config.BRAND === 'luxuryescapes' && productType === 'Hotels') {
    return intl.formatMessage(
      {
        id: 'H97kLCSq91dLUcgqHwac',
        defaultMessage: 'Escape with our luxury hotel deals in {location}',
        description: 'Subheading for luxury hotel destination product page with location',
      },
      { location: loc },
    )
  }
  switch (productType) {
    case 'Holiday Packages':
      return intl.formatMessage(
        {
          id: 'hqUS-EHuiA70YGRkd0jf',
          defaultMessage: 'Explore our Holiday Package deals in {location}',
          description: 'Subheading for holiday package destination product page with location',
        },
        { location: loc },
      )
    case 'Hotels':
      return intl.formatMessage(
        {
          id: 'hbVn_ACRw3YzwDuxZ4lh',
          defaultMessage: 'Explore our Hotel deals in {location}',
          description: 'Subheading for hotel destination product page with location',
        },
        { location: loc },
      )
    case 'Tours':
      return intl.formatMessage(
        {
          id: 'FMp6OHqMZxBiWC8NZ3MT',
          defaultMessage: 'Explore our Tour deals in {location}',
          description: 'Subheading for tour destination product page with location',
        },
        { location: loc },
      )
    case 'Holiday Homes':
      return intl.formatMessage(
        {
          id: 'eG7Tw2s8Pmw0l8lNQT6v',
          defaultMessage: 'Explore our Holiday Home deals in {location}',
          description: 'Subheading for holiday home destination product page with location',
        },
        { location: loc },
      )
    default:
      return intl.formatMessage(
        {
          id: 'aKAMS_CLSk5VF0qP1ZnJ',
          defaultMessage: '{baseSubheading} in {location}',
          description: 'Fallback destination product page subheading with location suffix',
        },
        { baseSubheading, location: loc },
      )
  }
}

function getSubheading(
  location: string | undefined,
  productType: string,
  canonicalName: string | undefined,
  intl: IntlShape,
): string {
  const loc = restoreCapitalisation(location, canonicalName)
  const baseSubheading = getBaseSubheading(productType, intl)
  if (loc) {
    return getSubheadingWithLocation(baseSubheading, productType, loc, intl)
  }
  return baseSubheading
}

function getHolidayPackageDescription(
  loc: string | undefined,
  holidayType: string | undefined,
  intl: IntlShape,
) {
  const year = new Date().getFullYear()
  const yearRange = `${year}/${year + 1}`

  if (loc && holidayType) {
    return intl.formatMessage(
      {
        id: '9jBPKj4nl8_vS3dTbGb-',
        defaultMessage: '{location} {holidayType} Holiday Package deals for travel in {yearRange}. Save on luxury hotel + flight bundles. Get exclusive inclusions.',
        description: 'Meta description for holiday package page with location and holiday type',
      },
      { location: loc, holidayType, yearRange },
    )
  }
  if (loc) {
    return intl.formatMessage(
      {
        id: 'ZCOn6mV8ZrRXRhX4UR6c',
        defaultMessage: '{location} Holiday Package deals for travel in {yearRange}. Save on luxury hotel + flight bundles. Get exclusive inclusions.',
        description: 'Meta description for holiday package page with location only',
      },
      { location: loc, yearRange },
    )
  }
  if (holidayType) {
    return intl.formatMessage(
      {
        id: 'Ow8THwLBwXNYiWQH4c8r',
        defaultMessage: '{holidayType} Holiday Package deals for travel in {yearRange}. Save on luxury hotel + flight bundles. Get exclusive inclusions.',
        description: 'Meta description for holiday package page with holiday type only',
      },
      { holidayType, yearRange },
    )
  }
  return intl.formatMessage(
    {
      id: '3pt-V6_AqJvipo7P3e4y',
      defaultMessage: 'Holiday Package deals for travel in {yearRange}. Save on luxury hotel + flight bundles. Get exclusive inclusions.',
      description: 'Meta description for holiday package page without location or holiday type',
    },
    { yearRange },
  )
}

function getDescription(
  location: string | undefined,
  holidayType: string | undefined,
  productType: string,
  canonicalName: string | undefined,
  intl: IntlShape,
) {
  const loc = restoreCapitalisation(location, canonicalName)
  const productTypeLabel = formatLandingPageProductName(intl, productType)
  if (productType === 'Holiday Packages') {
    return getHolidayPackageDescription(loc, holidayType, intl)
  }
  if (productType === 'Hotels') {
    if (loc) {
      return intl.formatMessage(
        {
          id: 'H9UOPVEDDjwliB9iuqie',
          defaultMessage: 'Book the most exclusive boutique, luxury and 5 star accommodation in {location} at the best prices.',
          description: 'Meta description for hotel destination product page with location',
        },
        { location: loc },
      )
    }
    return intl.formatMessage({
      id: 'SlXHJ-0E-cr6_PwD7Jcn',
      defaultMessage: 'Book the most exclusive boutique, luxury and 5 star accommodation at the best prices.',
      description: 'Meta description for hotel destination product page without location',
    })
  }
  const metaTitle = resolveLocalizedConfigValue(intl, config.head.metaTitleForDestinationProductPage)
  if (loc && holidayType) {
    return intl.formatMessage(
      {
        id: 'gD3qbz549bDgRSt04V9C',
        defaultMessage: 'Find the latest deals & exclusive special offers for {holidayType} {productType} in {location}. {metaTitle}.',
        description: 'Generic meta description for destination product page with holiday type and location',
      },
      {
        holidayType,
        productType: productTypeLabel,
        location: loc,
        metaTitle,
      },
    )
  }
  if (loc) {
    return intl.formatMessage(
      {
        id: 'fOVL-dig19V6zW7rLvUy',
        defaultMessage: 'Find the latest deals & exclusive special offers for {productType} in {location}. {metaTitle}.',
        description: 'Generic meta description for destination product page with location only',
      },
      {
        productType: productTypeLabel,
        location: loc,
        metaTitle,
      },
    )
  }
  if (holidayType) {
    return intl.formatMessage(
      {
        id: '-B2OS7bUVbgNUMcG0-kH',
        defaultMessage: 'Find the latest deals & exclusive special offers for {holidayType} {productType}. {metaTitle}.',
        description: 'Generic meta description for destination product page with holiday type only',
      },
      {
        holidayType,
        productType: productTypeLabel,
        metaTitle,
      },
    )
  }
  return intl.formatMessage(
    {
      id: 'UmP-7p5rS1y1iOLrJVTq',
      defaultMessage: 'Find the latest deals & exclusive special offers for {productType}. {metaTitle}.',
      description: 'Generic meta description for destination product page without location or holiday type',
    },
    {
      productType: productTypeLabel,
      metaTitle,
    },
  )
}

function getLuxHotelMetaDescription(productType: string, location: string | undefined, canonicalName: string | undefined, intl: IntlShape): string {
  const loc = restoreCapitalisation(location, canonicalName)

  const locations = loc?.split(',')
  const smallestGeoArea = locations?.[0] || ''
  const country = locations?.[locations.length - 1] || ''

  if (smallestGeoArea || country) {
    return intl.formatMessage(
      {
        id: '6IDL_dTMPpAmDicNFW3J',
        defaultMessage: 'Exclusive deals on luxury hotels & resorts in {smallestGeoArea},{country}. Enjoy 5-star accommodation at the best prices only with {brandTitle}.',
        description: 'Meta description for luxury hotel destination product page with location',
      },
      {
        smallestGeoArea,
        country,
        brandTitle: config.title,
      },
    )
  }
  return intl.formatMessage(
    {
      id: 'LuT2gGq3wck8GdHhA8iU',
      defaultMessage: 'Exclusive deals on luxury hotels & resorts. Enjoy 5-star accommodation at the best prices only with {brandTitle}.',
      description: 'Meta description for luxury hotel destination product page without location',
    },
    {
      brandTitle: config.title,
    },
  )
}

function getMetaHeadTitle(
  regionCode: string,
  location: string | undefined,
  holidayType: string | undefined,
  productType: string,
  canonicalName: string | undefined,
  isExperiment: boolean | undefined,
  intl: IntlShape,
) {
  if (isExperiment) {
    return getExperimentHolidayPackageHeadDataTitle(productType, holidayType, location, canonicalName, intl)
  }

  const isLuxHotel = isLuxuryEscapesHotel(productType)
  if (isLuxHotel) {
    return getLuxHotelHeadDataTitle(productType, location, canonicalName, intl)
  }
  else {
    const headTitle = getHeadTitle(location, holidayType, productType, canonicalName, intl)
    return intl.formatMessage(
      {
        id: 'LDslusDCanqkuVFUUv0Q',
        defaultMessage: '{headTitle} - {brandTitle} {regionCode}',
        description: 'Destination product page meta head title including brand and region code',
      },
      {
        headTitle,
        brandTitle: config.title,
        regionCode,
      },
    )
  }
}

function getMetaDescription(
  location: string | undefined,
  holidayType: string | undefined,
  productType: string,
  canonicalName: string | undefined,
  isExperiment: boolean | undefined,
  intl: IntlShape,
) {
  if (isExperiment) {
    return getExperimentHolidayPackageMetaDescription(productType, location, canonicalName, intl)
  }

  const isLuxHotel = isLuxuryEscapesHotel(productType)
  if (isLuxHotel) {
    return getLuxHotelMetaDescription(productType, location, canonicalName, intl)
  }
  return getDescription(location, holidayType, productType, canonicalName, intl)
}

function getHolidayPackageHeadTitle(loc: string | undefined, holidayType: string | undefined, intl: IntlShape): string {
  const year = new Date().getFullYear()
  const yearRange = `${year}/${year + 1}`

  if (loc && holidayType) {
    return intl.formatMessage(
      {
        id: 'DT12Tf9Xt1H1pksCzAo_',
        defaultMessage: '{location} {holidayType} Holidays & Package Deals {yearRange}',
        description: 'Head title for holiday package page with location and holiday type',
      },
      { location: loc, holidayType, yearRange },
    )
  }
  if (loc) {
    return intl.formatMessage(
      {
        id: '9lP6XSwElFn3mMgOL_b8',
        defaultMessage: '{location} Holidays & Package Deals {yearRange}',
        description: 'Head title for holiday package page with location only',
      },
      { location: loc, yearRange },
    )
  }
  if (holidayType) {
    return intl.formatMessage(
      {
        id: 'idUHhNXBgN9vFwaTc5mW',
        defaultMessage: '{holidayType} Holidays & Package Deals {yearRange}',
        description: 'Head title for holiday package page with holiday type only',
      },
      { holidayType, yearRange },
    )
  }
  return intl.formatMessage(
    {
      id: 'L2-IfRNU8SrGalNZzick',
      defaultMessage: 'Holidays & Package Deals {yearRange}',
      description: 'Head title for holiday package page without location or holiday type',
    },
    { yearRange },
  )
}

function getHotelHeadTitle(loc: string | undefined, holidayType: string | undefined, intl: IntlShape): string {
  if (config.BRAND === 'luxuryescapes') {
    if (loc) {
      return intl.formatMessage(
        {
          id: 'x65-boPxY4gYC5q83iYn',
          defaultMessage: 'Best Luxury and 5 Star Hotels and Resorts in {location}',
          description: 'Head title for luxury hotel page with location in the offer section',
        },
        { location: loc },
      )
    }
    return intl.formatMessage({
      id: 'LaUPJMNDP-THVdb92CcN',
      defaultMessage: 'Best Luxury and 5 Star Hotels and Resorts',
      description: 'Head title for luxury hotel page without location in the offer section',
    })
  }
  if (loc && holidayType) {
    return intl.formatMessage(
      {
        id: 'IC1Tz3CF4ANoPwjPtKMx',
        defaultMessage: '4 & 5 Star {holidayType} Hotels in {location}',
        description: 'Head title for non-luxuryescapes hotel page with holiday type and location',
      },
      { holidayType, location: loc },
    )
  }
  if (loc) {
    return intl.formatMessage(
      {
        id: 'NyyfkvPACJ1hrqPIsc3R',
        defaultMessage: '4 & 5 Star Hotels in {location}',
        description: 'Head title for non-luxuryescapes hotel page with location only',
      },
      { location: loc },
    )
  }
  if (holidayType) {
    return intl.formatMessage(
      {
        id: '1OW0Qlsajk3MGot_VbBQ',
        defaultMessage: '4 & 5 Star {holidayType} Hotels',
        description: 'Head title for non-luxuryescapes hotel page with holiday type only',
      },
      { holidayType },
    )
  }
  return intl.formatMessage({
    id: 'Sp7CE6LWelsHBfSbBcvx',
    defaultMessage: '4 & 5 Star Hotels',
    description: 'Head title for non-luxuryescapes hotel page without location or holiday type',
  })
}

function getHeadTitle(
  location: string | undefined,
  holidayType: string | undefined,
  productType: string,
  canonicalName: string | undefined,
  intl: IntlShape,
) {
  const loc = restoreCapitalisation(location, canonicalName)
  if (productType === 'Holiday Packages') {
    return getHolidayPackageHeadTitle(loc, holidayType, intl)
  }
  if (productType === 'Hotels') {
    return getHotelHeadTitle(loc, holidayType, intl)
  }
  return getTitle(location, holidayType, productType, canonicalName, intl)
}

function getSmallestGeoLocation(location: string | undefined, canonicalName?: string) {
  const loc = restoreCapitalisation(location, canonicalName)
  const smallestGeoArea = loc?.split(',')[0] || ''
  return smallestGeoArea
}

function getLuxHotelHeadDataTitle(productType: string, location: string | undefined, canonicalName: string | undefined, intl: IntlShape): string {
  const smallestGeoArea = getSmallestGeoLocation(location, canonicalName)
  if (smallestGeoArea) {
    return intl.formatMessage(
      {
        id: 'Gtkrqv96hzdmSNUQ2592',
        defaultMessage: '{location} Luxury and 5 Star Hotels and Resorts - {brandTitle}',
        description: 'Head title for luxury hotel destination product page with location and brand title',
      },
      {
        location: smallestGeoArea,
        brandTitle: config.title,
      },
    )
  }
  return intl.formatMessage(
    {
      id: 'GqD_Qt6oVPZOGJMyb3Hr',
      defaultMessage: 'Luxury and 5 Star Hotels and Resorts - {brandTitle}',
      description: 'Head title for luxury hotel destination product page without location',
    },
    {
      brandTitle: config.title,
    },
  )
}

function getExperimentHolidayPackageHeadDataTitle(
  productType: string,
  holidayType: string | undefined,
  location: string | undefined,
  canonicalName: string | undefined,
  intl: IntlShape,
): string {
  const smallestGeoArea = getSmallestGeoLocation(location, canonicalName)
  const year = new Date().getFullYear()
  const yearRange = `${year}/${year + 1}`

  if (smallestGeoArea && holidayType) {
    return intl.formatMessage(
      {
        id: 'RjvAlnEw6eGu3hjqCErl',
        defaultMessage: '{location} {holidayType} Holidays Packages & Deals {yearRange} - {brandTitle}',
        description: 'Head title for holiday package SEO experiment page with location and holiday type',
      },
      {
        location: smallestGeoArea,
        holidayType,
        yearRange,
        brandTitle: config.title,
      },
    )
  }
  if (smallestGeoArea) {
    return intl.formatMessage(
      {
        id: '4Ye8NUFaVxtmSp9D7cXy',
        defaultMessage: '{location} Holidays Packages & Deals {yearRange} - {brandTitle}',
        description: 'Head title for holiday package SEO experiment page with location only',
      },
      {
        location: smallestGeoArea,
        yearRange,
        brandTitle: config.title,
      },
    )
  }
  if (holidayType) {
    return intl.formatMessage(
      {
        id: 'ILwuVaXxh1eu74soZUMP',
        defaultMessage: '{holidayType} Holidays Packages & Deals {yearRange} - {brandTitle}',
        description: 'Head title for holiday package SEO experiment page with holiday type only',
      },
      {
        holidayType,
        yearRange,
        brandTitle: config.title,
      },
    )
  }
  return intl.formatMessage(
    {
      id: 'TI4rZSVHMR___iaE_WRC',
      defaultMessage: 'Holidays Packages & Deals {yearRange} - {brandTitle}',
      description: 'Head title for holiday package SEO experiment page without location or holiday type',
    },
    {
      yearRange,
      brandTitle: config.title,
    },
  )
}

function getExperimentHolidayPackageMetaDescription(productType: string, location: string | undefined, canonicalName: string | undefined, intl: IntlShape): string {
  const loc = restoreCapitalisation(location, canonicalName)

  const locations = loc?.split(',')
  const smallestGeoArea = locations?.[0] || ''
  const country = locations?.[locations.length - 1] || ''

  if (smallestGeoArea || country) {
    return intl.formatMessage(
      {
        id: 'ydHmzqvt7BfoUcdbWtGJ',
        defaultMessage: 'Discover the best {smallestGeoArea},{country} holiday package deals. Save on luxury hotel + flight packages, with exclusive inclusions only at {brandTitle}.',
        description: 'Meta description for holiday package SEO experiment page with location',
      },
      {
        smallestGeoArea,
        country,
        brandTitle: config.title,
      },
    )
  }
  return intl.formatMessage(
    {
      id: 'Psa0qYpt6HM9VIxWxZ3H',
      defaultMessage: 'Discover the best holiday package deals on exclusive luxury hotels & resorts. Save on luxury hotel + flight packages, with exclusive inclusions only at {brandTitle}.',
      description: 'Meta description for holiday package SEO experiment page without location',
    },
    {
      brandTitle: config.title,
    },
  )
}

function getTitle(
  location: string | undefined,
  holidayType: string | undefined,
  productType: string,
  canonicalName: string | undefined,
  intl: IntlShape,
) {
  return getDestinationProductPage(location, holidayType, productType, canonicalName, intl)?.title
}

function getRelatedTravelItems(
  location: string | undefined,
  holidayType: string | undefined,
  productType: string,
  canonicalName: string | undefined,
  intl: IntlShape,
): Array<{ text: string, url: { to: string } }> {
  const loc = restoreCapitalisation(location, canonicalName)
  return nonNullable(landingPageProductNames.map(productName => {
    const productNameLabel = formatLandingPageProductName(intl, productName)
    if (!holidayType && productName === productType) {
      return null
    }
    if (productName !== 'Hotels' && location && !placesShortlist.includes(location)) {
      return null
    }
    let text: string
    if (loc && holidayType) {
      text = intl.formatMessage(
        {
          id: '1-4TSF_j5Vyfpb1AhXjC',
          defaultMessage: '{location} {holidayType} {productName}',
          description: 'Related travel item text with location and holiday type',
        },
        { location: loc, holidayType, productName: productNameLabel },
      )
    } else if (loc) {
      text = intl.formatMessage(
        {
          id: '0yNYnSTq-XKlGepDxBBz',
          defaultMessage: '{location} {productName}',
          description: 'Related travel item text with location only in the offer section',
        },
        { location: loc, productName: productNameLabel },
      )
    } else if (holidayType) {
      text = intl.formatMessage(
        {
          id: 'kEGJxd4Jxt9ikdMx9DB_',
          defaultMessage: '{holidayType} {productName}',
          description: 'Related travel item text with holiday type only in the offer section',
        },
        { holidayType, productName: productNameLabel },
      )
    } else {
      text = productNameLabel
    }
    return {
      text,
      url: {
        to: `/${loc ? urlTransform(loc).toLowerCase() + '.' : ''}${holidayType ? urlTransform(holidayType).toLowerCase() + '.' : ''}${urlTransform(productName).toLowerCase()}`,
      },
    }
  }))
}

function isNotLowerCase(s: string): boolean {
  return s.toLowerCase() !== s
}

// For legacy reasons, this function returns a lower case location, and an upper cased holiday type and product type.
// If a redirect due to incorrect case is needed, shouldRedirect is set to true.
// Elsewhere in this file, capitalisation of the location is achieved using the restoreCapitalisation function.
function decodeUrlParams(params: { locationOrHolidayType?: string, location?: string, holidayType?: string, productType: string, city?: string, state?: string, country?: string }) {
  const { locationOrHolidayType, location, holidayType, productType, city, state, country } = params

  let holidayType_: string | undefined = undefined
  if (holidayType || locationOrHolidayType) {
    holidayType_ = escapeTypes[escapeTypesWithoutSpace.findIndex((e) => e.toLowerCase() === (holidayType ?? locationOrHolidayType ?? '').toLowerCase())]
  }

  let location_: string | undefined = humanTransform([location, city, state, country].filter(Boolean).join(','))

  location_ = location_ !== '' ? location_ : undefined

  if (!location_ && !!locationOrHolidayType && !holidayType_) {
    location_ = humanTransform(locationOrHolidayType)
  }

  const productType_ = landingPageProductNames[landingPageProductNamesWithoutSpace.findIndex((e) => e.toLowerCase() === productType.toLowerCase())]

  const shouldRedirect =
    isNotLowerCase(productType) ||
    (!!location_ && isNotLowerCase(location_)) ||
    (!!holidayType_ && isNotLowerCase(holidayType ?? locationOrHolidayType ?? '')) ||
    (productType_ === 'Hotels' && !country)

  if (productType_ !== 'Hotels' && !shouldRedirect) {
    const locationInnerLevel = location_?.split(', ')[0] ?? ''
    location_ = placesShortlist.includes(locationInnerLevel) ? location_ : undefined
  }

  return { location: location_, holidayType: holidayType_, productType: productType_, shouldRedirect }
}

// roll out SEO testing results into all the luxury Escapes  destination hotel pages
// Following logic will be used to determine if the page is related to the lux hotel
function isLuxuryEscapesHotel(productType: string): boolean {
  if (productType === 'Hotels') {
    if (config.BRAND === 'luxuryescapes') {
      return true
    }
  }
  return false
}

// SEO experiment for .holidaypackages pages
// following logic will be used to determine if the holidaypackages experiment is on
function isExperimentOn(url: string, productType: string): boolean {
  if (productType === 'Holiday Packages') {
    if (config.BRAND === 'luxuryescapes') {
      return url.length % 2 === 1
    }
  }
  return false
}

export { getDestinationProductPage, getDescription, getHeadTitle, getTitle, getRelatedTravelItems, getPageTypeSlug, decodeUrlParams, calculatePageTypes, getSubheading, restoreCapitalisation, getLuxHotelHeadDataTitle, getLuxHotelMetaDescription, isLuxuryEscapesHotel, getMetaHeadTitle, getMetaDescription, isExperimentOn }
